import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  People as CRMIcon,
  Add as AddIcon,
  Sync as SyncIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckIcon,
  TrendingUp as GrowthIcon,
  ContactPhone as ContactIcon,
  Business as LeadIcon
} from '@mui/icons-material';

interface CRMIntegrationsProps {
  integrations: any[];
  onRefresh: () => void;
  loading: boolean;
}

const CRMIntegrations: React.FC<CRMIntegrationsProps> = ({
  integrations,
  onRefresh,
  loading
}) => {
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [selectedCRM, setSelectedCRM] = useState('');
  const [syncSettings, setSyncSettings] = useState({
    sync_contacts: true,
    sync_leads: true,
    sync_opportunities: true,
    sync_accounts: true,
    bidirectional: true,
    frequency: 'HOURLY'
  });

  // CRM Systems
  const crmSystems = [
    {
      name: 'Salesforce',
      logo: '☁️',
      description: 'World\'s #1 CRM platform with comprehensive sales and marketing tools',
      features: ['Contact Management', 'Lead Tracking', 'Opportunity Pipeline', 'Sales Analytics'],
      pricing: 'From £20/user/month',
      status: 'available'
    },
    {
      name: 'HubSpot',
      logo: '🧡',
      description: 'Inbound marketing and sales platform with free tier available',
      features: ['Free CRM', 'Email Marketing', 'Lead Generation', 'Sales Automation'],
      pricing: 'Free tier available',
      status: 'available'
    },
    {
      name: 'Pipedrive',
      logo: '🔵',
      description: 'Sales-focused CRM designed for small and medium businesses',
      features: ['Pipeline Management', 'Activity Tracking', 'Sales Reporting', 'Mobile App'],
      pricing: 'From £12.50/user/month',
      status: 'available'
    },
    {
      name: 'Zoho CRM',
      logo: '🟠',
      description: 'Complete business suite with CRM, marketing, and support tools',
      features: ['Multi-channel CRM', 'Workflow Automation', 'Analytics', 'Integration Hub'],
      pricing: 'From £12/user/month',
      status: 'available'
    }
  ];

  // Mock CRM sync data
  const crmSyncData = {
    contacts_synced: 1247,
    leads_synced: 89,
    opportunities_synced: 34,
    last_sync: '2024-09-15T10:30:00Z',
    sync_errors: 0,
    data_quality_score: 94
  };

  // Mock customer data flow
  const customerDataFlow = [
    {
      source: 'CRM',
      destination: 'Financial System',
      data_type: 'Customer Profiles',
      records: 1247,
      last_sync: '2024-09-15T10:30:00Z',
      status: 'SUCCESS'
    },
    {
      source: 'Financial System',
      destination: 'CRM',
      data_type: 'Invoice Data',
      records: 156,
      last_sync: '2024-09-15T10:25:00Z',
      status: 'SUCCESS'
    },
    {
      source: 'CRM',
      destination: 'Financial System',
      data_type: 'Payment Terms',
      records: 89,
      last_sync: '2024-09-15T10:20:00Z',
      status: 'SUCCESS'
    }
  ];

  const handleSetupCRM = (crmName: string) => {
    setSelectedCRM(crmName);
    setShowSetupDialog(true);
  };

  const handleConnect = () => {
    // Simulate CRM connection
    setShowSetupDialog(false);
    onRefresh();
  };

  return (
    <Box>
      {/* CRM Integration Overview */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <ContactIcon color="primary" />
                <Box>
                  <Typography variant="body2" color="textSecondary">Contacts Synced</Typography>
                  <Typography variant="h6">{crmSyncData.contacts_synced.toLocaleString()}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <LeadIcon color="success" />
                <Box>
                  <Typography variant="body2" color="textSecondary">Leads Synced</Typography>
                  <Typography variant="h6">{crmSyncData.leads_synced}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <GrowthIcon color="info" />
                <Box>
                  <Typography variant="body2" color="textSecondary">Opportunities</Typography>
                  <Typography variant="h6">{crmSyncData.opportunities_synced}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <CheckIcon color="success" />
                <Box>
                  <Typography variant="body2" color="textSecondary">Data Quality</Typography>
                  <Typography variant="h6">{crmSyncData.data_quality_score}%</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Data Flow Status */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Customer Data Flow
          </Typography>
          
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Source</TableCell>
                  <TableCell>Destination</TableCell>
                  <TableCell>Data Type</TableCell>
                  <TableCell align="right">Records</TableCell>
                  <TableCell>Last Sync</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {customerDataFlow.map((flow, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {flow.source}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {flow.destination}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {flow.data_type}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {flow.records.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(flow.last_sync).toLocaleString('en-GB')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={flow.status}
                        color={flow.status === 'SUCCESS' ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Available CRM Systems */}
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">
              Available CRM Systems
            </Typography>
            <Button
              variant="outlined"
              startIcon={<SyncIcon />}
              onClick={onRefresh}
            >
              Refresh
            </Button>
          </Box>
          
          <Grid container spacing={3}>
            {crmSystems.map((crm, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <Typography variant="h4">
                        {crm.logo}
                      </Typography>
                      <Box>
                        <Typography variant="h6">
                          {crm.name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {crm.pricing}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Typography variant="body2" color="textSecondary" paragraph>
                      {crm.description}
                    </Typography>
                    
                    <Typography variant="body2" fontWeight="medium" gutterBottom>
                      Key Features:
                    </Typography>
                    <List dense>
                      {crm.features.map((feature, featureIndex) => (
                        <ListItem key={featureIndex} sx={{ px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckIcon color="success" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={feature}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                    
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => handleSetupCRM(crm.name)}
                      fullWidth
                      sx={{ mt: 2 }}
                    >
                      Connect {crm.name}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* CRM Setup Dialog */}
      <Dialog open={showSetupDialog} onClose={() => setShowSetupDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <CRMIcon />
            Connect {selectedCRM}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Secure Integration:</strong> Your {selectedCRM} credentials are encrypted and stored securely. 
              We only access the data you authorize.
            </Typography>
          </Alert>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="API Key / Username"
                placeholder="Enter your API credentials"
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="password"
                label="API Secret / Password"
                placeholder="Enter your API secret"
                margin="normal"
              />
            </Grid>
          </Grid>
          
          <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
            Sync Settings
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={syncSettings.sync_contacts}
                    onChange={(e) => setSyncSettings({...syncSettings, sync_contacts: e.target.checked})}
                  />
                }
                label="Sync Contacts"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={syncSettings.sync_leads}
                    onChange={(e) => setSyncSettings({...syncSettings, sync_leads: e.target.checked})}
                  />
                }
                label="Sync Leads"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={syncSettings.sync_opportunities}
                    onChange={(e) => setSyncSettings({...syncSettings, sync_opportunities: e.target.checked})}
                  />
                }
                label="Sync Opportunities"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={syncSettings.bidirectional}
                    onChange={(e) => setSyncSettings({...syncSettings, bidirectional: e.target.checked})}
                  />
                }
                label="Bidirectional Sync"
              />
            </Grid>
          </Grid>
          
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Sync Frequency</InputLabel>
            <Select
              value={syncSettings.frequency}
              label="Sync Frequency"
              onChange={(e) => setSyncSettings({...syncSettings, frequency: e.target.value})}
            >
              <MenuItem value="REAL_TIME">Real-time</MenuItem>
              <MenuItem value="HOURLY">Hourly</MenuItem>
              <MenuItem value="DAILY">Daily</MenuItem>
              <MenuItem value="WEEKLY">Weekly</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSetupDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleConnect}>
            Connect & Test
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CRMIntegrations;
